import React, { useRef, PropsWithChildren } from "react";
import { gtm } from "helpers/analytics";
import { useOnWatch } from "helpers/analytics";
import styles from "./TrackedBanner.module.scss";

type Props = PropsWithChildren<{
  banner: Entity.Banner;
  single?: boolean;
  isGtmExperimentAvailable?: boolean;
}>;

const TrackedBanner: React.FC<Props> = ({
  banner,
  single,
  isGtmExperimentAvailable,
  children,
}: Props) => {
  const itemRef = useRef(null);
  const tracked = useRef(false);

  const onBannerWatch = async () => {
    if (!banner.counters?.imps?.length) return;
    if (
      !single &&
      !itemRef.current?.parentElement?.className.includes("swiper-slide-active")
    )
      return;

    isGtmExperimentAvailable && gtm.onAsideBannerWatch();
    gtm.onLunaBannerWatch(banner.counters);

    try {
      await Promise.all(
        banner.counters.imps.map(imp => {
          return fetch(imp);
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  const trackItem = async event => {
    if (tracked.current || !banner.counters?.track?.length) return;

    tracked.current = true;

    const itemHasBeenViewed =
      event &&
      ([0, 1].includes(event.button) ||
        ((event.ctrlKey || event.shiftKey || event.metaKey) &&
          event.button === 0));

    if (itemHasBeenViewed) {
      isGtmExperimentAvailable && gtm.onAsideBannerClick();
      gtm.onLunaBannerClick(banner.counters);

      try {
        await Promise.all(
          banner.counters?.track.map(track => {
            return fetch(track);
          })
        );
      } catch (err) {
        console.error(err);
      }
    }
  };

  useOnWatch({
    subject: banner,
    onWatch: onBannerWatch,
    elementRef: itemRef,
  });

  return (
    <div
      ref={itemRef}
      className={styles.banner}
      onMouseUp={trackItem}
      onClick={trackItem}
    >
      {children}
    </div>
  );
};

export default TrackedBanner;
